import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { api } from "@/api/client";

import { getTerms, termsSpeciesKeyFromSlug } from "@/lib/speciesTerms";
import { SERVICE_CATALOG } from "@/lib/serviceCatalog";
import { logger } from "@/utils/logger";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const BrowseServiceRegister = ({ kind }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const species = location.pathname.split('/')[2]; // /browse/:species/.../register
  const query = useQuery();

  const role = query.get('role');
  const type = query.get('type');

  const title = (() => {
    if (kind === 'breeding') {
      const key = termsSpeciesKeyFromSlug(species);
      const { male, female } = getTerms(key);
      const term = role === 'female' ? female : male;
      return `Register as a ${term}`;
    }

    const svc = SERVICE_CATALOG.find((s) => s.type === type);
    return svc ? `Register as a ${svc.title}` : 'Register';
  })();

  const [formData, setFormData] = useState({
    displayName: "",
    description: "",
    locationCity: "",
    locationState: "",
    locationZip: "",
    phone: "",
    website: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/signin');
      return;
    }

    if (!formData.displayName.trim()) {
      setError("Display name is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        kind,
        species,
        display_name: formData.displayName,
        description: formData.description || null,
        location_city: formData.locationCity || null,
        location_state: formData.locationState || null,
        location_zip: formData.locationZip || null,
        phone: formData.phone || null,
        website: formData.website || null
      };

      if (kind === 'service') {
        payload.service_type = type;
      } else if (kind === 'breeding') {
        payload.role = role;
      }

      const response = await api.post('/service-providers', payload);
      
      setSuccess(true);
      setTimeout(() => {
        navigate(`/browse/${species}`);
      }, 2000);
    } catch (err) {
      logger.error('Registration error:', err);
      setError(err.response?.data?.detail || 'Failed to register. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">
          {success ? "Registration Complete" : "Service Registration"}
        </p>
        <h1 className="text-4xl text-[#EDEDED] mb-4">{title}</h1>
        <p className="text-[#A1A1AA] mb-8">
          {success 
            ? "Your registration has been submitted successfully. Redirecting..." 
            : "Complete the form below to register your service."}
        </p>

        {success ? (
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 text-[#D4AF37]">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-lg">Registration successful! You can now be found by buyers.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass-card p-6 space-y-6">
            {/* Display Name */}
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">
                Display Name <span className="text-[#EF4444]">*</span>
              </label>
              <input
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                className="input-minimal"
                placeholder="Your program or business name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="input-minimal min-h-[100px] resize-none"
                placeholder="Tell buyers about your services..."
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">City</label>
                <input
                  name="locationCity"
                  value={formData.locationCity}
                  onChange={handleChange}
                  className="input-minimal"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">State</label>
                <input
                  name="locationState"
                  value={formData.locationState}
                  onChange={handleChange}
                  className="input-minimal"
                  placeholder="State"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Zip Code</label>
                <input
                  name="locationZip"
                  value={formData.locationZip}
                  onChange={handleChange}
                  className="input-minimal"
                  placeholder="Zip"
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Phone</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-minimal"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Website</label>
                <input
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="input-minimal"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="border-t border-[#27272A] pt-4">
              <p className="text-sm text-[#71717A] mb-2">Registration Summary:</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#71717A]">Species</p>
                  <p className="text-[#EDEDED] capitalize">{species}</p>
                </div>
                {role && (
                  <div>
                    <p className="text-[#71717A]">Role</p>
                    <p className="text-[#EDEDED] capitalize">{role}</p>
                  </div>
                )}
                {type && (
                  <div>
                    <p className="text-[#71717A]">Service Type</p>
                    <p className="text-[#EDEDED] capitalize">{type}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444] rounded-lg p-4 text-[#EF4444] text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-2">
              <button 
                type="submit" 
                className="btn-gold text-sm"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Registration"}
              </button>
              <Link to={`/browse/${species}`} className="btn-secondary text-sm">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BrowseServiceRegister;
